import {isLoaded} from 'adapter/loading.adapter';
import Input from 'components/common/form/input/Input';
import {API_ROUTES} from 'constants/illustrator';
import {UpfrontDrawQueryKey} from 'constants/query';
import {useProductContext} from 'contexts/product-context';
import {ToNumber} from 'lib/utils';
import {useCallback, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import {useFormContext} from 'react-hook-form';
import {useQuery} from 'react-query';

function useUpfrontDrawQuery(props) {
  const {
    upfrontDrawPercentage: UDP,
    MaxRevolvingLoc: MRL,
    PrincipalLimit: PL,
    loading,
    ApiRoute,
    ...rest
  } = props;

  const upfrontDrawPercentage = useMemo(() => ToNumber(UDP), [UDP]);
  const MaxRevolvingLoc = useMemo(() => ToNumber(MRL), [MRL]);
  const PrincipalLimit = useMemo(() => ToNumber(PL), [PL]);

  const queryKey = useMemo(
    () => [UpfrontDrawQueryKey, upfrontDrawPercentage, MaxRevolvingLoc, PrincipalLimit],
    [upfrontDrawPercentage, MaxRevolvingLoc, PrincipalLimit],
  );

  return useQuery({
    queryKey,
    enabled: isLoaded({loading}) && ApiRoute === API_ROUTES.SELECT,
    queryFn() {
      return new Promise((resolve) => {
        const TotalUpfrontDraw = ToNumber(MaxRevolvingLoc * (upfrontDrawPercentage / 100));

        return resolve({
          LineOfCredit: ToNumber(PrincipalLimit - TotalUpfrontDraw),
        });
      });
    },
    ...rest,
  });
}

function useUpfrontDrawPercentage() {
  const {product, loading} = useProductContext();
  const {watch, setValue, triggerSubmit} = useFormContext();

  const [upfrontDrawPercentage, MaxRevolvingLoc, PrincipalLimit, LineOfCredit] = watch([
    'upfrontDrawPercentage',
    'MaxRevolvingLoc',
    'PrincipalLimit',
    'LineOfCredit',
  ]);

  const ApiRoute = useMemo(() => product?.ApiRoute, [product?.ApiRoute]);

  const ref = useRef();
  useImperativeHandle(
    ref,
    () => ({
      LineOfCredit,
    }),
    [LineOfCredit],
  );

  const select = useCallback(
    (response) => ({
      LineOfCredit: ToNumber(response.LineOfCredit),
    }),
    [],
  );

  const {data = {}} = useUpfrontDrawQuery({
    upfrontDrawPercentage,
    MaxRevolvingLoc,
    PrincipalLimit,
    loading,
    ApiRoute,
    select,
  });

  useEffect(() => {
    Object.keys(data).forEach((key) => {
      if (ToNumber(ref.current?.[key]) !== ToNumber(data[key])) {
        setValue(key, data[key], {shouldValidate: false});
      }
    });
  }, [data, setValue]);

  const onChangeCommitted = useCallback(() => {
    // Slider is the only place this hook writes TotalUpfrontDraw.
    const pct = ToNumber(upfrontDrawPercentage);
    const maxLoc = ToNumber(MaxRevolvingLoc);
    const total = maxLoc > 0 ? ToNumber(maxLoc * (pct / 100)) : 0;

    setValue('TotalUpfrontDraw', total, {shouldValidate: false});
    triggerSubmit();
  }, [triggerSubmit, setValue, upfrontDrawPercentage, MaxRevolvingLoc]);

  return {onChangeCommitted};
}

function UpfrontDrawPercentage(props) {
  const {onChangeCommitted} = useUpfrontDrawPercentage();

  return <Input {...props} name="upfrontDrawPercentage" type="range" onChangeCommitted={onChangeCommitted} />;
}

export default UpfrontDrawPercentage;